/**
 * @file 配置
 * @author dafo<huanghouin@baijiahulian.com>
 * @module util/config
 */

import isClient from './detectClient';
import cookie from './cookie';
import {extend, toQuery} from './json';
import getEnv, {isInGaoTuApp, isTestReg} from './env';
import loadScript from './loadScript';
import delay from './delay';
import getReqIdx from './requestIndex';
import uuid from './uuid';
import {detectStorage, areCookiesEnabled} from './detectStorageAvailability';
import {commonParamsFromApp, isPlainObj} from './appCommonParam';
import {initInterceptXHR} from './interceptAjax';

const {hostname = ''} = isClient && location;

// 用于临时验证线上日志是否符合预期，手动在本地中加一个cookie isSwitchTestToProd 不为空即可
const isSwitchTestToProd = cookie.get('isSwitchTestToProd');

// 上报的域名
let DOMAIN = '//i.gsxtj.com/';
let QINGZHOU_DOMAIN = '//qingzhou-fe.baijia.com/';

if (isTestReg.test(hostname) && !isSwitchTestToProd) {
    DOMAIN = DOMAIN.replace('//', '//test-');
    QINGZHOU_DOMAIN = QINGZHOU_DOMAIN.replace('//', '//test-');
}

/**
 * 上报的公共字段 name 的值
 *
 * @const {string}
 */
const NAME = (
    host => (
        {
            baijiahulian: ''
        }[host] || host
    )
)(hostname.replace(/^[^.]+\.|(100)?\.[^.]+$/g, ''));


const ENV = isClient ? getEnv().env : 0;

// 生成会话级别sessionid
export const HB_SESSION_ID_KEY = 'hbsessionid';

if (isClient) {
    // 设置会话级别的cookie，用于会话级别的数据跟踪分析
    !cookie.get(HB_SESSION_ID_KEY) && cookie.set(HB_SESSION_ID_KEY, uuid());
}


/**
 * 上报路径相关的配置
 *
 * @enum {string|number}
 */
export const Config = {

    /**
     * 上报域名
     */
    DOMAIN,

    /**
     * 上报域名
     */
    QINGZHOU_DOMAIN,

    /**
     * 项目标识名，格式：业务线-产品标识
     */
    NAME,

    /**
     * 宿主标识
     */
    ENV,

    /**
     * 配置提交上报的 HTTP VERB
     */
    VERB: 'post',

    /**
     * PV 的上报路径
     */
    PV: 'pv0.gif',

    /**
     * 点击、展开的上报路径
     */
    GS: 'gs.gif',

    /**
     * 性能相关指标上报路径
     */
    PERF: 'perf.gif',

    /**
     * 时间统计相关的上报路径
     */
    TIME: 'time.gif',

    /**
     * 数据追踪的上报路径
     */
    TRACE: 'trace.gif',

    /**
     * 页面停留时长的上报路径
     */
    STAY: 'stay.gif',

    /**
     * 批量上报，主用于视频相关的上报
     */
    BATCH: 'batch_event.gif',

    /**
     * metric 服务请求路径，用于上报点展滚动等数据
     */
    METRIC: 'metric.gif',

    /**
     * gid 服务请求路径
     */
    GID: 'habo/gid.js',

    /**
     * log 业务日志上报到轻舟
     */
    LOG: 'log.gif',

    /**
     * 是否延迟发送请求，默认开启
     */
    DELAY: true,

    /**
     * 监控模式，默认 1 => 为不监控, 还可以是 2 => hash、3 => history
     */
    ROUTE_MODE: 3,

    /**
     * 业务相关的用户id， 存于Cookie中的名字， 比如， uid、 user_id、 __user_id__等， 可以支持业务方根据自己情况自定义。 用于后续可以用此id来快速定位问题
    */
    UID_COOKIE_NAME: 'uid',

    /**
     * 轻舟上用来区分不同应用的标识，取自擎天云上服务标识nid
     */
    APPNID: '',

    /**
     * 上报接口性能数据到轻舟开关
     */
    // ENABLE_QINGZHOU_PERF: true,

    /**
     * 默认超时时间 0, 不超时，一直等待
     */
    TIMEOUT: 0,

    /**
     * 接口拦截开关，默认开启
     */
    ALLOW_INTERCEPT: true,

    /**
     * 接口拦截规则
     */
    INTERCEPT_RULES: {
        include: [],
        exclude: [],
    },

    /**
     * 慢请求阀值
     */
    SLOW_REQUEST_THRESHOLD: 0
};

/**
 * 全局追踪统计的用户唯一标识
 *
 * @type {string}
 */
export const trackID = isClient ? cookie.get('__track_id__') : cookie.get('GID');

/**
 * 登录户标识，默认取 user_id
 *
 * @type {string}
 */
export const getUserID = () => (isClient
    ? Config.GET_USER_ID && typeof Config.GET_USER_ID === 'function'
        // eslint-disable-next-line babel/new-cap
        ? Config.GET_USER_ID()
        : cookie.get(Config.UID_COOKIE_NAME)
    : '');

/**
 * 需要透传的公共参数，比如 abtest等
 */

export const publicParams = {};

/**
 * 全局附加字段
 *
 * @type {Object}
 */
export const extraData = {
    gid: '',
    ...commonParamsFromApp
};

/**
 * 注意 NAME 上报， 注意本方法已废弃！！
 *
 * @function
 * @param {string} queryString 要注入的源数据
 * @return {string} 返回注入 name=NAME 后的源数据
 */
export const injectCommonField = (queryString, addionalParams) => {
    if (!queryString) {
        return '';
    }

    // 注入业务线-产品代号
    // 如果自定义的参数中含有 name，取自定义的参数中的 name， 否则使用 Config.NAME
    if (!/(?:&|^)name=/.test(queryString) && !extraData.name) {
        if (!Config.NAME) {
            // eslint-disable-next-line no-console
            console.warn('no habo field `name`!');
        }
        // extraData中有客户端name字段,优先使用，覆盖配置项
        queryString += `&name=${encodeURIComponent(Config.NAME)}`;
    }

    // 注入客户端类型
    if (!/(?:&|^)env=/.test(queryString)) {
        queryString += `&env=${encodeURIComponent(Config.ENV)}`;
    }

    queryString = `${queryString.replace(/(&|^)ver=[^&]+/g, '')}&ver=${VERSION}`;

    queryString += `&${toQuery(extraData)}`;

    if (!/((?:^|&)t=)([^$&]*)/i.test(queryString)) {
        const now = (+new Date()).toString(36);
        queryString += `&t=${now}`;
    }

    const {type} = addionalParams;
    // 注入 req_idx ，用于统计丢失率
    queryString += `&req_idx=${getReqIdx(type, extraData.gid)}`;

    return queryString.replace(/^&+/, '');
};

/**
 * 将公共字段和自定义数据合并，替换原有的 injectCommonField，在字符串上追加字段的不优雅难维护的操作
 *
 * @param {Object} params 自定义的上报信息
 * @param {Object} addionalParams 公共的上报信息
 * @return {Object} 合并之后的参数
 */
export function extendCommonField(params, addionalParams) {
    const {type = ''} = addionalParams;
    // 严格控制顺序，
    // 公共参数中的 name 可以覆盖掉自定义参数中的name，此操作与上面原逻辑不一致，原逻辑可能导致自定义参数中的 name，污染原配置中的业务线名，在此优化
    // env、ver、t、req_idx是 h5 哈勃自有需要上报的保留字段
    // extraData 是 gid，和客户端传过来的参数
    // 客户端传过来的参数 有以下 10 个（app_type,habo_did,p_cuid,app_version,app_terminal,device_os,app_channel,name,device_platform,did）, 需要覆盖原有的name， did，其他 8 个字段，暂不与哈勃本身字段重复，如果重复，则用客户端公参值覆盖掉原有的值

    return {
        ...params,
        name: encodeURIComponent(Config.NAME),
        env: encodeURIComponent(Config.ENV),
        ver: VERSION,
        t: (+new Date()).toString(36),
        req_idx: getReqIdx(type, extraData.gid),
        ...extraData
    };
}

let configFirstInitFlag = false;
/**
 * 第一次setConfig的时候，初始化操作
 */
const handleConfig = () => {
    // 只处理第一次setConfig的时候
    if (!configFirstInitFlag) {
        configFirstInitFlag = true;
        // 多取几次appNid是为了兼容历史传参情况
        const appNid = Config.APPNID || Config.appNid || Config.appId;
        // 当开关打开且有appNid的时候，初始化拦截器
        if (Config.ALLOW_INTERCEPT && appNid) {
            initInterceptXHR();
        }
    }
}
/**
 *  覆盖配置
 * @function
 * @param {Object} defaults 覆盖上报相关的配置
 * @return {Object} 更新后的 Config
 */
export const setConfig = defaults => {
    const result = extend(Config, defaults);
    handleConfig();
    return result;
};

/**
 * 设置用于透传上报的公共参数,
 * publicParams 数据会打平，只有一层，目的是兼容 get 请求发送数据格式、或者 post 请求 application/x-www-form-urlencoded 格式数据（key=value）
 *
 * @param {object} params 自定义的公共参数
 */
export const setPublicParams = params => {
    if (isPlainObj(params)) {
        const publicParamsTemp = {};
        Object.keys(params).forEach(key => {
            // 在这里暂不 encodeURIComponent ，因为后续参数会自动通过 toQuery 进行 encodeURIComponent， 防止多次 encode 后，解码麻烦
            params[key] && (publicParamsTemp[key] = JSON.stringify(params[key]));
        });
        Object.assign(publicParams, publicParamsTemp);
    }
};

let readyHandlerList = [];

/**
 * 判断config是否初始化完毕
 */
export const isReady = () => readyHandlerList === null;

/**
 * 注册初始化完毕回调
 *
 * @param {Function} 回调函数
 */
export const onReady = handler => {
    if (isReady()) {
        handler();
        return;
    }
    readyHandlerList.push(handler);
};

const execOnReady = () => {
    readyHandlerList && readyHandlerList.forEach(handler => handler());
    readyHandlerList = null;
};

/**
 * 设置临时的cookie GID
 */
function setTempGID() {
    const tempGID = uuid();
    extraData.gid = tempGID;
    // 在隐私模式，禁用cookie下，读写localStorage 或 cookie，会报错
    if (detectStorage() && areCookiesEnabled()) {
        try {
            localStorage.setItem('GID', tempGID);

            //  cookie 写在根域名下，最大程度的保证设备的唯一性
            const rootDomainArr = location.hostname.split('.');
            if (/\d+/.test(rootDomainArr[rootDomainArr.length - 1])) {
                return;
            }

            const rootDomain = rootDomainArr.slice(-2).join('.');
            document.cookie = `GID=${tempGID};domain=${rootDomain};path=/;max-age=3153600000;`;
        }
        catch (e) {
            console.warn('setTempGID err', e);
        }
    }
}

/**
 * 初始化全局用户相关参数
 */
export const initExtraData = () => {
    if (!isClient) {
        return;
    }

    try {
        extraData.gid = cookie.get('GID') || localStorage.getItem('GID');
    }
    catch (e) {
        console.warn('读取GID出错');
    }

    // 如果是在客户端，用于覆盖掉h5哈勃的gid
    if (isInGaoTuApp && commonParamsFromApp.gid) {
        extraData.gid = commonParamsFromApp.gid;
    }

    // 在测试环境的日志都会追加一个字段，上报当前项目的分支等信息，用于测试环境埋点治理检测
    if (isTestReg.test(hostname)) {
        const imageName = cookie.get('_app_br_');
        imageName && (extraData.qingzhou_image_name = imageName);
    }

    if (extraData.gid || !isClient) {
        execOnReady();
        return;
    }

    // 如果 cookie 中拿不到GID
    const cookieEnabled = areCookiesEnabled();

    // 如果 cookie 不可用，就尝试在 localStorage 中设置一下
    if (cookieEnabled) {
        delay(() => {
            // 通过file协议访问本地静态文件时手动拼接https协议请求cdn上gid.js文件
            const isFileProtocol = document && document.location.protocol === 'file:';
            const GIDUrl = isFileProtocol ? `https:${Config.DOMAIN}${Config.GID}` : `${Config.DOMAIN}${Config.GID}`;
            loadScript(GIDUrl, () => {
                extraData.gid = cookie.get('GID');
                if (extraData.gid) {
                    execOnReady();
                }
                else {
                    setTempGID();
                    execOnReady();
                }
            });
        })();
    }
    // 请求GID
    else {
        setTempGID();
        execOnReady();
    }
};

// 是否已经包装过 pushState/replaceState
let IS_WRAPPED = false;
export const getRouteChangeEventIsWrapped = () => IS_WRAPPED;
export const setRouteChangeEventWrapped = () => {
    IS_WRAPPED = true;
};

/**
 * 路由模式
 *
 * @enum {Number}
 */
export const ROUTE_MODE = {
    // 监控模式，默认 1
    SERVER: 1,

    // 页面通过 hash 切换路由
    HASH: 2,

    // 页面通过 history API 切换路由
    HISTORY: 3
};

/**
 * 页面渲染类型
 * 主要用于确定页面停留时长的上报或统计时机
 *
 * @enum {Number}
 */
export const RenderType = {
    // 服务端渲染的，每次跳转页面都会刷新
    SERVER: 1,

    // 页面通过 hash 切换路由
    HASH: 2,

    // 页面通过 history API 切换路由
    HISTORY: 3
};

// 当前浏览器支持的 hidden 和 visibilityChange 写法
const [hidden, visibilityChange] = [
    ['hidden', 'visibilitychange'],
    ['webkitHidden', 'webkitvisibilitychange'],
    ['mozHidden', 'mozvisibilitychange'],
    ['msHidden', 'msvisibilitychange']
].filter(([key]) => key in (isClient && document || {}))[0] || [];

export const documentHidden = hidden;
export const documentVisibilityChange = visibilityChange;

// 不会拦截下面该域名的接口
export const DISABLE_INTERCEPT_API_DOAMIN_LIST = [
    Config.DOMAIN,
    Config.QINGZHOU_DOMAIN
];

// 用于哈勃sdk的内部事件前缀
export const HABO_EVENT_PREFIX = 'habo_';
// sdk尽量不派发公共全局事件，如果需要sdk内部派发事件，事件命名统一加前缀，防止与业务中的事件冲突
export const getInnerEventName = eventName => HABO_EVENT_PREFIX + eventName;

